import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      // Endpoint is intentionally never a hard 4xx to prevent email enumeration.
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="forgot-password-page">
      <SEO title="Reset your password" description="Reset your Point Penta staff account password." path="/forgot-password" noIndex />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-6 w-full">
        <Link to="/" className="flex items-center gap-3" data-testid="forgot-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_penta-realty-uae/artifacts/lk46xfkn_Point%20Penta%20Logo%20png.png"
            alt="Point Penta"
            className="h-11 w-auto"
          />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md border border-[#e5e7eb] p-10 bg-white">
          {!done ? (
            <form onSubmit={submit} data-testid="forgot-password-form">
              <div className="overline text-[#001588] mb-3">Recovery</div>
              <h1 className="font-serif text-3xl mb-3 text-[#050d19]">Forgot your ID or password?</h1>
              <p className="text-sm text-neutral-600 leading-relaxed mb-6">
                Enter the email you use to sign in. We&apos;ll send a secure reset link — valid for 60 minutes — to that inbox and to our backup admin address so we can help you recover access if needed.
              </p>
              <label className="block mb-6">
                <span className="overline text-neutral-500 block mb-2">Email</span>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border border-[#e5e7eb] px-4 py-3 focus:outline-none focus:border-[#001588]"
                  data-testid="forgot-email" placeholder="you@pointpenta.com"
                />
              </label>
              <button type="submit" disabled={busy}
                className="w-full py-3 bg-[#001588] text-white tracking-wide hover:bg-black transition-colors disabled:opacity-50"
                data-testid="forgot-submit">
                {busy ? "Sending link…" : "Send reset link"}
              </button>
              <div className="flex items-center justify-between mt-4">
                <Link to="/login" className="text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-[#001588]">
                  ← Back to sign in
                </Link>
                <a href="mailto:immuzammil8@gmail.com" className="text-xs uppercase tracking-[0.18em] text-[#001588] hover:underline">
                  Contact admin
                </a>
              </div>
            </form>
          ) : (
            <div data-testid="forgot-password-confirmation">
              <CheckCircle size={40} className="text-[#001588] mb-4" weight="thin" />
              <div className="overline text-[#001588] mb-3">Check your inbox</div>
              <h1 className="font-serif text-3xl mb-3 text-[#050d19]">Reset link sent.</h1>
              <p className="text-sm text-neutral-600 leading-relaxed mb-6">
                If an account exists for <b>{email}</b>, a password reset link has been emailed to that address and to our backup admin recovery address. The link expires in 60 minutes.
              </p>
              <div className="bg-[#ffffff] border border-[#e5e7eb] p-4 flex items-start gap-3 mb-6">
                <EnvelopeSimple size={20} className="text-[#001588] mt-0.5 flex-shrink-0" weight="thin" />
                <p className="text-xs text-neutral-600 leading-relaxed">
                  Didn&apos;t receive it? Check your spam folder, or email <a href="mailto:immuzammil8@gmail.com" className="text-[#001588] underline">immuzammil8@gmail.com</a> directly for manual recovery.
                </p>
              </div>
              <Link to="/login" className="block text-center w-full py-3 border border-[#001588] text-[#001588] tracking-wide hover:bg-[#001588] hover:text-white transition-colors">
                Return to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
